import moment from "moment";
import { DELIVERY, AFFILIATE_COOKIE, API_ROOT } from "../constants";
import md5 from "md5";
import axios from "axios";

export function getCookies() {
	let result = {};
	document.cookie
		.split(";")
		.map(pair => pair.trim().split("="))
		.forEach(([name, value]) => {
			result[name] = value
		});
	return result;
}

export function setCookie({ key, value, path = "/", expires = moment().add(1, "month") }) {
	const cookie = [
		[key, value || ""],
		path ? ["path", path] : null,
		expires ? ["expires", moment(expires).toDate()] : null
	]
		.filter(c => c)
		.map(([key, value]) => `${key}=${value}`)
		.join("; ");
	document.cookie = cookie;
}

/**
 * 
 * @param {Object} object The object with key value pairs to turn into a 
 * query string.
 * 
 * @returns {String} The query string. Does not contain preceding '?' 
 * character. 
 */
export function objectToQuery(object) {
	return Object.entries(object)
		.filter(([, value]) => value !== null)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join("&");
}

/**
 * 
 * @param {Array} removeFromQueryString The field names to remove from 
 * the query string. This does not remove them from the resulting
 * parsed onject, it updates the query string in the URL to exclude
 * them.
 * 
 * @returns {Object} The query string parsed into an object.
 */
export function getQuery(removeFromQueryString = []) {
	// Build the object from the query string.
	let result = parseQueryString(window.location.search);
	// If query properties are to be removed, do it here.
	if (removeFromQueryString.length) {
		let allowedValues = { ...result };
		removeFromQueryString.forEach(key => delete allowedValues[key]);
		let newQs = objectToQuery(allowedValues);
		let newUrl = window.location.href.replace(window.location.search, newQs ? "?" + newQs : "");
		// Replace the actual history state to one without the code.
		if (window.history && window.history.replaceState)
			window.history.replaceState({}, "", newUrl);
	}
	return result;
}

export function parseQueryString(qs = "") {
	const result = {}
	qs.replace(/^\?/, "")
		.split("&")
		.map(pair => pair.split("="))
		.forEach(([key, value]) => {
			if (value)
				result[decodeURIComponent(key)] = decodeURIComponent(value);
		});
	return result;
}

/**
 * Generates an array of length 'length', which starts from 'startsFrom' and increases
 * by 1 for each index. IE incrementingArray(4, 1) returns [1, 2, 3, 4].
 * 
 * @param {Integer} length How long you'd like the array to be.
 * @param {Integer} startsFrom The first number in the array, and starting point for incrementing.
 */
export function incrementingArray(length = 10, startsFrom = 1) {
	return [...new Array(length)].map((i, index) => index + startsFrom);
}

/**
 * 
 * @param {Array} array Array to group.
 * @param {*} field The field that the arrays should be grouped by.
 * 
 * Takes an array and returns an object, where each key of the object
 * is a unique field, and the value is an array containing all items
 * with that matching field. 
 */
export function groupArrayByField(array, field) {
	const result = {};
	array.forEach(item => {
		if (!result[item[field]]) {
			result[item[field]] = [item];
		} else {
			result[item[field]].push(item);
		}
	});
	return result;
}

export function addressToString(address = {}) {
	return [
		address.line1,
		address.line2,
		address.town,
		address.postCode
	].filter(a => a).join(", ");
}

export function totalLineItem({ quantity, options, product }, { addOns }) {
	const optionTotal = options.reduce((acc, option) => {
		const addOn = addOns.find(a => a.id === option.addOn);
		// If the addon wasn't found, assume 0 for now.
		return (addOn ? addOn.price * option.quantity : 0) + acc;
	}, 0);
	return (optionTotal + product.price) * quantity;
}

export function totalBasket(basket, vendor) {
	let itemTotal = basket.items.reduce((acc, item) => acc + totalLineItem(item, vendor), 0);
	let deliveryPrice = basket.type === DELIVERY ? getDeliveryPrice(vendor.deliveryRules, itemTotal) : 0;
	let fee = 50;
	return [
		(itemTotal + deliveryPrice + fee),
		itemTotal,
		deliveryPrice,
		fee
	];
}

export function getDeliveryPrice(rules, total) {
	// End now if no rules.
	if (!rules || !rules.length) return 0;
	// First sort rules by price, ascending.
	rules.sort((a, b) => b.orderValue - a.orderValue);
	for (let i = 0; i < rules.length; i++) {
		// And if the order passes the test, we have our delivery price.
		if (total >= rules[i].orderValue)
			return rules[i].deliveryPrice;
	}
	return 0;
}

export function errorResponse(error) {
	let message;
	if (error.response) {
		message = error.response.data.message;
		if (message instanceof Array) {
			message = message.map(m => m.messages.map(m2 => m2.message).join(" ")).join(" ");
		}
	} else if (error.request) {
		message = "No response from server."
	} else if (error.message) {
		message = error.message;
	} else {
		message = "Unknown error.";
		console.error("Unknown error: ", error);
	}
	return message;
}

export function gravatar(email = "") {
	const hash = md5(email.trim().toLowerCase());
	return `https://www.gravatar.com/avatar/${hash}?d=mp`;
}

export function payloadToFormData(payload) {
	// Construct formdata.
	const data = {};
	const formdata = new FormData();
	Object.entries(payload).forEach(([key, value]) => {
		if (value instanceof Blob || value instanceof File) {
			formdata.append(`files.${key}`, value);
		} else if (value instanceof Array && value.length) {
			data[key] = [];
			value.forEach(item => {
				if (item instanceof Blob || value instanceof File) {
					formdata.append(`files.${key}`, item);
				} else {
					data[key].push(item);
				}
			});
		} else {
			data[key] = value;
		}
	});
	formdata.append("data", JSON.stringify(data));
	return formdata;
}

export function delay(timeout = 500) {
	return new Promise(resolve => {
		setTimeout(resolve, timeout);
	});
}

export function roundUpMinutes(date = moment()) {
	// Takes a moment date object
	let minutes = Math.ceil(date.minutes() / 10) * 10;
	return date.set("minute", minutes);
}

export function isOpen(availableTimes, time = moment()) {
	// Check if open today
	const dayOfWeek = time.format("ddd").toUpperCase();
	let dayMatches = availableTimes.filter(time => time.day === dayOfWeek);
	// not available today
	if (!dayMatches.length) return false;
	// available today, check availableTimes
	return Boolean(dayMatches.find(match => {

		let from = splitTime(match.from);
		let to = splitTime(match.to);

		return time.isSameOrAfter(time.clone().set({ hour: from[0], minute: from[1] })) &&
			time.isSameOrBefore(time.clone().set({ hour: to[0], minute: to[1] }))
	}));
}

export function splitTime(timeSlot) {
	const parts = timeSlot.split(":");
	return [Number(parts[0]), Number(parts[1])];
}

export function twoDp(number) {
	return String(number).length === 1 ? "0" + number : number;
}

export const timeSlots = [...new Array(24)].map((i, h) => {
	const hour = twoDp(h);
	return [...new Array(6)].map((j, minute) => `${hour}:${minute ? twoDp(minute * 10) : "00"}`)
}).flat();

export function getAffiliate() {
	return getCookies()[AFFILIATE_COOKIE] || null;
}

export function setAffiliate(code) {
	setCookie({
		key: AFFILIATE_COOKIE,
		value: code,
		expires: code ? moment().add(8, "hours") : moment().add(1, "year"),
		path: "/"
	});
}

export function formatDate(date) {
	if (date.isSame(moment(), "day")) return `Today ${date.format("[at] HH:mm")}`;
	return date.format(`DD MMM [at] HH:mm`);
}

export function nextAvailableTime(openingTimes = [], minimumTime = 0, relativeTo = moment()) {

	const date = roundUpMinutes(relativeTo.clone());

	// If there aren't any opening hours, we're done.
	if (!openingTimes.length) return null;

	let slotMatch;
	// Create a moment for the day we're on.
	let currentDay = date.clone();
	// Iterate maximum of 8 days.
	for (let i = 0; i < 8; i++) {
		// Extract the weekday.
		const dayOfWeek = currentDay.format("ddd").toUpperCase();
		// Then attempt to find the first slot allowed.
		slotMatch = openingTimes
			// Filter out other days.
			.filter(slot => slot.day === dayOfWeek)
			// Sort by starting time.
			.sort((a, b) => Number(a.from.replace(":", "")) - Number(b.from.replace(":", "")))
			// Filter out any that have already ended.
			.find(slot => {
				const [hours, minutes] = splitTime(slot.to);
				const endOfSlot = currentDay.clone().set({ hours, minutes });
				return date.clone().add(minimumTime, "minutes").isSameOrBefore(endOfSlot);
			});

		if (slotMatch) break;
		// Increase the current day.
		currentDay.add(1, "days");
	}

	// If there wasn't a slot available, we're done
	if (!slotMatch) return null;

	// If there was one, we just need to check it hasn't started before now.
	const [hours, minutes] = splitTime(slotMatch.from);
	const startOfSlot = currentDay.clone().set({ hours, minutes });
	// If it was after the start, add on the minimum time, and we're done.
	if (date.isAfter(startOfSlot)) {
		return date.add(minimumTime, "minutes");
	} else {
		// Otherwise, the start of the slot is fine.
		return startOfSlot;
	}
}

export function vendorPath(slug) {
	return `/with/${slug}`;
}

export function nameToSlug(name) {
	return name
		.toLowerCase()
		.replace(/ /g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/^-/, "")
		.replace(/-{2,}/g, "-")
		.trim();
}

const placesCache = {};
export function getPlaces(query, token, locationOrigin) {
	return new Promise((resolve, reject) => {
		if (!query) return resolve([]);
		if (placesCache[query]) return resolve(placesCache[query]);
		const params = { query };
		if (locationOrigin) params.origin = locationOrigin;
		axios.get(`${API_ROOT}/misc/address/lookup`, {
			params,
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
			.then(res => {
				placesCache[query] = res.data;
				resolve(res.data);
			})
			.catch(err => reject(err.message));
	});
}

const placeDetailCache = {};
export function getPlaceDetails(id, token) {
	return new Promise((resolve, reject) => {
		if (placeDetailCache[id]) return resolve(placeDetailCache[id]);
		axios.get(`${API_ROOT}/misc/address/details/${id}`, {
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
			.then(res => {
				placeDetailCache[id] = res.data;
				resolve(res.data);
			})
			.catch(err => reject(err.message));
	});
}

export function rgbToString({ r, g, b, a }) {
	return `rgba(${r}, ${g}, ${b}${a ? `, ${a}` : ""})`;
}

const vendorSlugCache = {};
export function checkVendorSlug(slug, token) {
	return new Promise((resolve, reject) => {
		if (vendorSlugCache[slug] !== undefined) return resolve(vendorSlugCache[slug]);
		axios.get(`${API_ROOT}/vendors/check-slug`, {
			params: {
				slug
			},
			headers: {
				Authorization: `Bearer ${token}`
			}
		})
			.then(res => {
				vendorSlugCache[slug] = res.data;
				resolve(res.data);
			})
			.catch(err => reject(errorResponse(err)));
	});
}

export function readFile(file) {
	return new Promise(resolve => {
		const fr = new FileReader();
		fr.addEventListener('load', () => resolve(fr.result), false);
		fr.readAsDataURL(file);
	});
}

export function smoothScroll(container, target) {
	if (!container || !target) return;
	if (typeof container.scroll === "function") {
		container.scroll({
			top: getOffset(target).top - 60,
			behavior: "smooth"
		});
	} else {
		container.scrollTo(getOffset(target).top, 0);
	}
}

export function getOffset(el) {
	const rect = el.getBoundingClientRect(),
		scrollLeft = window.pageXOffset || document.documentElement.scrollLeft,
		scrollTop = window.pageYOffset || document.documentElement.scrollTop;
	return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
}

export function mergeObjectArrays(sourceArray, newArray, comparator = () => false) {
	// Create the final array we'll use as a clone of the source.
	let finalArray = [...sourceArray];
	// Iterate the new array.
	newArray.forEach(newItem => {
		// Reference for if we found something to merge with.
		let didMerge = false;
		finalArray = finalArray.map(oldItem => {
			// If the items are a match based on the comparator.
			if (comparator(oldItem, newItem)) {
				// Flag that a merge happened.
				didMerge = true;
				// Merge them, overwriting old with new.
				return {
					...oldItem,
					...newItem
				}
			} else {
				// Otherwise just return the original.
				return oldItem;
			}
		});
		// If no merge happened, add the new item to the end.
		if (!didMerge) {
			finalArray = [...finalArray, newItem];
		}
	});
	// Finally, we can return the new merged array.
	return finalArray;
}

function standardiseLineItemOptions(options) {
	// turns the options array into something that can be reliably compared.
	return options
		.map(({ name, addOn, quantity }) => ({ name, addOn, quantity }))
		.sort((a, b) => {
			if (a.name !== b.name) {
				if (a.name > b.name) {
					return -1;
				} else {
					return 1;
				}
			} else if (a.addOn !== b.addOn) {
				return a.addOn - b.addOn;
			} else {
				return a.quantity - b.quantity;
			}
		})
		.map(({ name, addOn, quantity }) => `${name}_${addOn}_${quantity}`)
		.join(".")
}

// Compares two line items to see if they are compatible.
export function isTheSameLineItem(a, b) {
	// Product IDs obviously must be the same.
	if (a.product.id !== b.product.id) return false;
	// Options must also be the same.
	// Quick exit if different lengths.
	if (a.options.length !== b.options.length) return false;
	// Otherwise, let's attampt to standardise the array and compare it.
	return standardiseLineItemOptions(a.options) === standardiseLineItemOptions(b.options);
}